import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, RefreshCw, Pencil, Trash2, Sparkles, Check, X, ExternalLink, ImageOff } from "lucide-react";
import { api, errorMessage, formatDate, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/ImageUpload";

const FIELDS = [["club_name", "Club"], ["nickname", "Nickname"], ["founded", "Founded"], ["current_league", "League"], ["stadium", "Stadium"], ["city", "City"], ["country", "Country"], ["latitude", "Lat"], ["longitude", "Lng"], ["blurb", "Blurb"]];
const SAVE_FIELDS = [...FIELDS.map(([k]) => k), "logo_url"];

export default function ClubMapAdmin() {
  const [maps, setMaps] = useState(null);
  const [jobs, setJobs] = useState({});
  const load = () => api.get("/admin/club-map").then((r) => setMaps(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const plot = async (showId) => {
    try {
      await api.post(`/admin/club-map/${showId}/plot`);
      setJobs((j) => ({ ...j, [showId]: true }));
      toast.success("Plotting new episodes in the background…");
      const poll = setInterval(async () => {
        const { data } = await api.get(`/admin/club-map/${showId}/plot`);
        if (!data.running) { clearInterval(poll); setJobs((j) => ({ ...j, [showId]: false })); load(); toast.success(`Done — ${data.result?.added || 0} new pin${data.result?.added === 1 ? "" : "s"}${data.result?.failed?.length ? `, ${data.result.failed.length} failed` : ""}`); }
      }, 4000);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!maps) return <p className="text-slate-400">Loading…</p>;
  return (
    <div data-testid="club-map-admin">
      <p className="eyebrow">Shows</p>
      <h1 className="font-display text-4xl font-black tracking-tight">Club Map</h1>
      <p className="mt-2 text-slate-400 text-sm">Pins are extracted from each episode with AI and geocoded via OpenStreetMap. New episodes are plotted automatically every Monday, Wednesday and Friday at 5:00 AM ET. Fix anything that looks off below — edits are kept on future runs.</p>
      {maps.length === 0 && <p className="mt-8 text-slate-400">No show has the club map switched on. Enable it under Shows → edit show.</p>}
      {maps.map((m) => <ShowPins key={m.show.id} data={m} running={jobs[m.show.id]} onPlot={() => plot(m.show.id)} onChanged={load} />)}
    </div>
  );
}

const ShowPins = ({ data, running, onPlot, onChanged }) => {
  const { show, pins, episode_count } = data;
  const missing = episode_count - pins.length;
  return (
    <section className="mt-8" data-testid={`club-map-show-${show.slug}`}>
      <div className="card-surface rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3"><Globe2 size={20} className="text-sky-400" /><div><p className="font-semibold">{show.name}</p><p className="text-xs text-slate-400" data-testid="club-map-counts">{pins.length} of {episode_count} episodes pinned{missing > 0 ? ` · ${missing} waiting` : ""}</p></div></div>
        <div className="flex items-center gap-2">
          <a href={`/shows/${show.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">View map <ExternalLink size={12} /></a>
          <Button onClick={onPlot} disabled={running} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="club-map-plot-button"><RefreshCw size={15} className={`mr-2 ${running ? "animate-spin" : ""}`} /> {running ? "Plotting…" : "Plot new episodes now"}</Button>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {pins.map((p, i) => <PinRow key={p.id} pin={p} index={i} onChanged={onChanged} />)}
      </div>
    </section>
  );
};

const PinRow = ({ pin, index, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(pin);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(pin), [pin]);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(SAVE_FIELDS.map((k) => [k, k === "latitude" || k === "longitude" ? (form[k] === "" || form[k] == null ? null : Number(form[k])) : form[k] ?? ""]));
      await api.put(`/admin/club-map/pins/${pin.id}`, body); toast.success("Pin updated"); setEdit(false); onChanged();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const redo = async () => {
    if (!window.confirm(`Re-run the AI lookup for "${pin.club_name}"? Your manual edits on this pin will be replaced.`)) return;
    setBusy(true);
    try { await api.post(`/admin/club-map/pins/${pin.id}/redo`); toast.success("Pin refreshed"); onChanged(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the pin for "${pin.club_name}"? It will be re-created on the next auto-plot unless the episode is gone.`)) return;
    try { await api.delete(`/admin/club-map/pins/${pin.id}`); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="card-surface rounded-2xl p-4" data-testid={`club-pin-${index}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-3">
          {pin.logo_url ? <img src={imgUrl(pin.logo_url)} alt="" className="h-10 w-10 shrink-0 rounded-lg bg-white object-contain p-1" data-testid={`club-pin-crest-thumb-${index}`} /> : <div className="h-10 w-10 shrink-0 rounded-lg bg-white/5 grid place-items-center text-slate-500" title="No crest yet" data-testid={`club-pin-crest-missing-${index}`}><ImageOff size={14} /></div>}
        <div className="min-w-0">
          <p className="font-semibold truncate" data-testid={`club-pin-name-${index}`}>{pin.club_name} {pin.status === "defunct" && <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-mono uppercase text-slate-300">defunct</span>}</p>
          <p className="text-xs text-slate-400 truncate">{[pin.founded && `est. ${pin.founded}`, pin.current_league, pin.stadium, [pin.city, pin.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</p>
          <p className="text-[11px] text-slate-500 truncate">Episode: {pin.episode?.title || "—"}{pin.episode?.published_at ? ` (${formatDate(pin.episode.published_at)})` : ""} · {pin.latitude?.toFixed(4)}, {pin.longitude?.toFixed(4)} · {pin.geo_source}</p>
        </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => setEdit((v) => !v)} className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white" title="Edit" data-testid={`club-pin-edit-${index}`}><Pencil size={15} /></button>
          <button type="button" onClick={redo} disabled={busy} className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white" title="Re-run AI lookup" data-testid={`club-pin-redo-${index}`}><Sparkles size={15} /></button>
          <button type="button" onClick={remove} className="rounded-full p-2 text-slate-400 hover:bg-rose-500/20 hover:text-rose-300" title="Remove pin" data-testid={`club-pin-delete-${index}`}><Trash2 size={15} /></button>
        </div>
      </div>
      {edit && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`club-pin-form-${index}`}>
          <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-5 rounded-xl bg-white/[0.03] p-3" data-testid={`club-pin-crest-${index}`}>
            <ImageUpload value={form.logo_url} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} shape="square" fit="contain" hint={false} testId={`club-pin-crest-upload-${index}`} />
            <div className="text-xs text-slate-400 max-w-xs">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Crest</p>
              <p className="mt-1">Drop a PNG/SVG-style crest with a transparent background for best results. It shows on the globe pin and the club card.</p>
            </div>
          </div>
          {FIELDS.map(([k, label]) => (
            <label key={k} className={k === "blurb" ? "sm:col-span-2 lg:col-span-3" : ""}><span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
              <input value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 h-9 w-full rounded-md bg-white/5 border border-white/10 px-3 text-sm" data-testid={`club-pin-input-${k}-${index}`} />
            </label>
          ))}
          <label><span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Status</span>
            <select value={form.status || "active"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="mt-1 h-9 w-full rounded-md bg-white/5 border border-white/10 px-3 text-sm"><option value="active" className="bg-[#0B1A33]">Active</option><option value="defunct" className="bg-[#0B1A33]">Defunct</option></select>
          </label>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3 justify-end">
            <Button type="button" variant="ghost" onClick={() => { setEdit(false); setForm(pin); }} className="rounded-full hover:bg-white/10"><X size={14} className="mr-1" /> Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid={`club-pin-save-${index}`}><Check size={14} className="mr-1" /> {busy ? "Saving…" : "Save pin"}</Button>
          </div>
        </div>
      )}
    </div>
  );
};
